import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import type { Category, CreateCategory } from '../types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

interface CategoryModalProps {
  category: Category | null;
  onClose: () => void;
  onSave: (category: Category | CreateCategory) => void;
}

interface FormData {
  id: string;
  name: string;
  slug: string;
  description: string;
  thumbnailUrl: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export function CategoryModal({ category, onClose, onSave }: CategoryModalProps) {
  const [localFormData, setLocalFormData] = useState<FormData>({
    id: '',
    name: '',
    slug: '',
    description: '',
    thumbnailUrl: '',
    isActive: true,
    createdAt: '',
    updatedAt: '',
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (category) {
      setLocalFormData({
        id: category.id,
        name: category.name,
        slug: category.slug,
        description: category.description || '',
        thumbnailUrl: category.thumbnailUrl || '',
        isActive: category.isActive,
        createdAt: category.createdAt,
        updatedAt: category.updatedAt,
      });
    } else {
      setLocalFormData({
        id: '',
        name: '',
        slug: '',
        description: '',
        thumbnailUrl: '',
        isActive: true,
        createdAt: '',
        updatedAt: '',
      });
    }
  }, [category]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      if (category) {
        // Editing existing category
        await onSave({
          ...localFormData,
          id: category.id,
          createdAt: category.createdAt,
          updatedAt: category.updatedAt,
        } as Category);
      } else {
        // Creating new category - only send CreateCategory fields
        const createData: CreateCategory = {
          name: localFormData.name,
          slug: localFormData.slug,
          description: localFormData.description,
          thumbnailUrl: localFormData.thumbnailUrl,
          isActive: localFormData.isActive,
        };
        await onSave(createData);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setLocalFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleStatusChange = (value: string) => {
    setLocalFormData(prev => ({ ...prev, isActive: value === 'true' }));
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{category ? 'Edit Category' : 'Add New Category'}</DialogTitle>
          <DialogDescription>
            {category ? 'Update category details' : 'Create a new category'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Category Name</Label>
            <Input
              id="name"
              name="name"
              value={localFormData.name}
              onChange={handleChange}
              required
              placeholder="Enter category name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="slug">Slug</Label>
            <Input
              id="slug"
              name="slug"
              value={localFormData.slug}
              onChange={handleChange}
              required
              placeholder="Enter category slug (e.g., electronics)"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="thumbnailUrl">Thumbnail URL</Label>
            <Input
              id="thumbnailUrl"
              name="thumbnailUrl"
              value={localFormData.thumbnailUrl}
              onChange={handleChange}
              placeholder="Enter thumbnail image URL"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              name="description"
              value={localFormData.description}
              onChange={handleChange}
              required
              rows={3}
              placeholder="Enter category description"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select value={String(localFormData.isActive)} onValueChange={handleStatusChange}>
              <SelectTrigger id="status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Active</SelectItem>
                <SelectItem value="false">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </form>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving || !localFormData.name || !localFormData.slug}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {category ? 'Updating...' : 'Adding...'}
              </>
            ) : (
              <>{category ? 'Update' : 'Add'} Category</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
